import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Mail, Lock, User, Building2, ArrowRight, Phone, CheckCircle, Shield, ArrowLeft, Eye, EyeOff } from "lucide-react";
import api from "../lib/api";
import { CURRENCIES } from "../lib/currency";

const Register = () => {
  const [step, setStep] = useState(1); // 1=contact, 2=verify, 3=details
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "business_admin",
    business_name: "",
    gst_number: "",
    currency: "INR",
  });
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register, addBusiness } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Step 1: Send OTPs
  const handleSendOtp = async () => {
    if (!formData.email || !formData.phone) {
      toast.error("Both email and phone number are required");
      return;
    }
    setSendingOtp(true);
    try {
      const res = await api.post("/otp/send", { email: formData.email, phone: formData.phone });
      toast.success("OTP sent to your email and phone!");
      // In development, show OTPs via debug
      if (res.data._debug_email_otp) {
        toast.info(`Email OTP: ${res.data._debug_email_otp}`, { duration: 15000 });
      }
      if (res.data._debug_phone_otp) {
        toast.info(`Phone OTP: ${res.data._debug_phone_otp}`, { duration: 15000 });
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  // Step 2: Verify OTPs
  const handleVerifyEmail = async () => {
    if (!emailOtp || emailOtp.length !== 6) { toast.error("Enter 6-digit email OTP"); return; }
    setVerifying(true);
    try {
      await api.post("/otp/verify", { target: formData.email, otp: emailOtp, type: "email" });
      setEmailVerified(true);
      toast.success("Email verified!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email OTP");
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneOtp || phoneOtp.length !== 6) { toast.error("Enter 6-digit phone OTP"); return; }
    setVerifying(true);
    try {
      await api.post("/otp/verify", { target: formData.phone, otp: phoneOtp, type: "phone" });
      setPhoneVerified(true);
      toast.success("Phone verified!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid phone OTP");
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setSendingOtp(true);
    try {
      const res = await api.post("/otp/send", { email: formData.email, phone: formData.phone });
      toast.success("OTP resent!");
      if (res.data._debug_email_otp) toast.info(`Email OTP: ${res.data._debug_email_otp}`, { duration: 15000 });
      if (res.data._debug_phone_otp) toast.info(`Phone OTP: ${res.data._debug_phone_otp}`, { duration: 15000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  // Step 3: Complete registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_name.trim()) { toast.error("Business name is required"); return; }
    if (!formData.gst_number.trim()) { toast.error("GST number is required"); return; }
    setLoading(true);
    try {
      const userData = {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        phone: formData.phone,
      };
      await register(userData);

      // Create the business (mandatory)
      const bizResponse = await api.post("/businesses", {
        name: formData.business_name,
        currency: formData.currency,
        gst_number: formData.gst_number,
      });
      addBusiness(bizResponse.data);

      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 mesh-gradient relative overflow-hidden">
        <div className="absolute inset-0 subtle-glow"></div>
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <div className="max-w-md text-center">
            <div className="mb-8">
              <h1 className="text-5xl font-bold text-slate-800 font-heading tracking-tight">
                Tribe<span className="text-primary">HR</span>
              </h1>
              <p className="text-slate-600 mt-2 text-lg">Human Resource Management</p>
            </div>
            <img
              src="https://images.unsplash.com/photo-1762652345565-e38e420b6f30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxzb2Z0JTIwYWJzdHJhY3QlMjAzRCUyMHNoYXBlcyUyMHBhc3RlbHxlbnwwfHx8fDE3NzIwOTQ4OTh8MA&ixlib=rb-4.1.0&q=85"
              alt="Abstract shapes"
              className="w-full max-w-sm mx-auto rounded-3xl shadow-2xl"
            />
            <p className="text-slate-500 mt-8 text-base leading-relaxed">
              Join thousands of businesses managing their HR operations efficiently with TribeHR.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 font-heading">
              Tribe<span className="text-primary">HR</span>
            </h1>
          </div>

          <div className="glass rounded-3xl p-8 shadow-xl">
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step >= s ? "bg-primary text-white" : "bg-slate-100 text-slate-400"
                  }`} data-testid={`step-indicator-${s}`}>
                    {step > s ? <CheckCircle className="h-4 w-4" /> : s}
                  </div>
                  {s < 3 && <div className={`w-8 h-0.5 ${step > s ? "bg-primary" : "bg-slate-200"}`} />}
                </div>
              ))}
            </div>

            {/* STEP 1: Contact Info */}
            {step === 1 && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-semibold text-slate-800 font-heading">Verify Your Identity</h2>
                  <p className="text-slate-500 mt-1 text-sm">We'll send OTPs to verify your email and phone</p>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <Input
                        name="email" type="email" placeholder="you@company.com"
                        value={formData.email} onChange={handleChange}
                        className="input-field w-full" style={{ paddingLeft: '2.5rem' }}
                        required data-testid="register-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <Input
                        name="phone" type="tel" placeholder="+91 9876543210"
                        value={formData.phone} onChange={handleChange}
                        className="input-field w-full" style={{ paddingLeft: '2.5rem' }}
                        required data-testid="register-phone-input"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleSendOtp} disabled={sendingOtp || !formData.email || !formData.phone}
                    className="w-full btn-primary h-12 text-base" data-testid="send-otp-btn"
                  >
                    {sendingOtp ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                        Sending OTP...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">Send OTP <ArrowRight className="h-5 w-5" /></span>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: OTP Verification */}
            {step === 2 && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-semibold text-slate-800 font-heading">Enter OTP</h2>
                  <p className="text-slate-500 mt-1 text-sm">Verify codes sent to your email and phone</p>
                </div>
                <div className="space-y-4">
                  {/* Email OTP */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" /> Email OTP
                      {emailVerified && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={emailOtp} onChange={e => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit code" className="input-field flex-1" maxLength={6}
                        disabled={emailVerified} data-testid="email-otp-input"
                      />
                      <Button
                        onClick={handleVerifyEmail} disabled={emailVerified || verifying || emailOtp.length !== 6}
                        className={emailVerified ? "bg-green-500 hover:bg-green-500 text-white" : "btn-primary"}
                        data-testid="verify-email-btn"
                      >
                        {emailVerified ? <CheckCircle className="h-4 w-4" /> : "Verify"}
                      </Button>
                    </div>
                  </div>

                  {/* Phone OTP */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" /> Phone OTP
                      {phoneVerified && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={phoneOtp} onChange={e => setPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit code" className="input-field flex-1" maxLength={6}
                        disabled={phoneVerified} data-testid="phone-otp-input"
                      />
                      <Button
                        onClick={handleVerifyPhone} disabled={phoneVerified || verifying || phoneOtp.length !== 6}
                        className={phoneVerified ? "bg-green-500 hover:bg-green-500 text-white" : "btn-primary"}
                        data-testid="verify-phone-btn"
                      >
                        {phoneVerified ? <CheckCircle className="h-4 w-4" /> : "Verify"}
                      </Button>
                    </div>
                  </div>

                  <button
                    type="button" onClick={handleResendOtp} disabled={sendingOtp}
                    className="text-sm text-primary hover:underline w-full text-center" data-testid="resend-otp-btn"
                  >
                    {sendingOtp ? "Resending..." : "Didn't receive? Resend OTP"}
                  </button>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="back-to-step1">
                      <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)} disabled={!emailVerified || !phoneVerified}
                      className="flex-1 btn-primary" data-testid="continue-to-step3"
                    >
                      Continue <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Account Details */}
            {step === 3 && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-semibold text-slate-800 font-heading">Business Details</h2>
                  <p className="text-slate-500 mt-1 text-sm">Complete your account setup</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                        <Input name="first_name" placeholder="John" value={formData.first_name} onChange={handleChange}
                          className="input-field w-full" style={{ paddingLeft: '2.5rem' }} required data-testid="register-firstname-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">Last Name</Label>
                      <Input name="last_name" placeholder="Doe" value={formData.last_name} onChange={handleChange}
                        className="input-field w-full" required data-testid="register-lastname-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <Input name="password" type={showPassword ? "text" : "password"} placeholder="Min 6 characters" value={formData.password}
                        onChange={handleChange} className="input-field w-full" style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
                        required minLength={6} data-testid="register-password-input" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        data-testid="toggle-register-password-visibility"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Your Role</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                      <SelectTrigger className="input-field w-full" data-testid="register-role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                        <SelectItem value="business_admin">Business Admin</SelectItem>
                        <SelectItem value="hr_manager">HR Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Business Name <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <Input name="business_name" placeholder="Acme Inc." value={formData.business_name} onChange={handleChange}
                        className="input-field w-full" style={{ paddingLeft: '2.5rem' }} required data-testid="register-business-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">GST Number <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Shield className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <Input name="gst_number" placeholder="22AAAAA0000A1Z5" value={formData.gst_number} onChange={handleChange}
                        className="input-field w-full" style={{ paddingLeft: '2.5rem' }} required data-testid="register-gst-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                      <SelectTrigger className="input-field" data-testid="register-currency-select">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="back-to-step2">
                      <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <Button type="submit" className="flex-1 btn-primary h-12" disabled={loading} data-testid="register-submit-btn">
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                          Creating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">Create Account <ArrowRight className="h-5 w-5" /></span>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <p className="text-center mt-6 text-slate-500 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
